import { useState } from "react";

import { Table } from "@mantine/core"
import { IconLink } from "@tabler/icons-react";
import { TFunction } from "i18next";

import { AgentChangeModal, CommentsModal, Link, PublicLinkModal } from "components";
import { AuthorityConstants, ServiceConstants } from "constant";
import { CreditService, UserService } from "services";
import { ClientListResponse, UserSimpleResponse } from "types";
import { UserUtils } from "utils";

import ClientAssignAgentModal from "./ClientAssignAgentModal";
import { ClientStatusFilter } from "./ClientStatusFilterBar";

const { ROLE_CR_R, ROLE_USR_CMT_R, ROLE_USR_CMT_R_MY, ROLE_CR_C, ROLE_USR_AGT_REASSIGN } = AuthorityConstants;
const { REQUEST_CREDIT_LINK_CREATE_ID, REQUEST_USER_AGENT_CHANGE_ID } = ServiceConstants;

interface ClientListItemProps {
  item: ClientListResponse;
  clientRoute: string;
  clientStatus: ClientStatusFilter;
  onAssigned: () => void;
  t: TFunction;
}

const ClientListItem = ({ item, clientRoute, clientStatus, onAssigned, t }: ClientListItemProps) => {
  const { user, credit, acquisitionPhase } = item;
  const [commentsVisible, setCommentsVisible] = useState<boolean>(false);
  const [linkVisible, setLinkVisible] = useState<boolean>(false);
  const [agentVisible, setAgentVisible] = useState<boolean>(false);
  const [numberOfComments, setNumberOfComments] = useState<number>(user.numberOfComments);
  const [agent, setAgent] = useState<UserSimpleResponse | undefined>(user.agent);
  const isUnassigned = clientStatus === "UNASSIGNED";

  return (
    <Table.Tr key={user.id}>
      <Table.Td>
        {UserUtils.hasAuthority(ROLE_CR_R) ?
          <Link to={`${clientRoute}/${user.id}`}>
            {user.fullName}
          </Link>
          : user.fullName}
      </Table.Td>
      <Table.Td visibleFrom={'md'}>{user.gender && user.gender.charAt(0)}</Table.Td>
      <Table.Td>{user.countryCode + ' ' + user.phoneNumber}</Table.Td>
      <Table.Td visibleFrom={'md'}>{credit?.partnerLastName ? credit.partnerLastName : undefined}</Table.Td>
      <Table.Td visibleFrom={'md'}>
        {credit?.partnerCountryCode && credit?.partnerPhoneNumber
          ? (credit.partnerCountryCode + ' ' + credit.partnerPhoneNumber)
          : undefined}
        </Table.Td>
      <Table.Td visibleFrom={'md'}>{credit?.city ?? user.city}</Table.Td>
      <Table.Td>{user.source && t('source.options.' + user.source)}</Table.Td>
      <Table.Td>{t('clients.list.tab.' + acquisitionPhase.toLowerCase())}</Table.Td>
      {user && user.lastActivity ? (
        <>
        <Table.Td visibleFrom={'md'}>{user.lastActivity && t('format.time', { date: new Date(user.lastActivity) })}</Table.Td>
        <Table.Td visibleFrom={'md'}>{user.lastActivity && t('format.date', { date: new Date(user.lastActivity) })}</Table.Td>
        </>
      ) : <Table.Td visibleFrom={'md'} colSpan={2} />}
      <Table.Td>{credit && t('credit.status.name.' + credit.status)}</Table.Td>
      <Table.Td ta={'center'}>
        {UserUtils.hasAnyAuthority([ROLE_USR_CMT_R, ROLE_USR_CMT_R_MY]) ?
          <>
            <Link onClick={() => setCommentsVisible(true)}>{numberOfComments}</Link>
            <CommentsModal
              opened={commentsVisible}
              onClose={() => setCommentsVisible(false)}
              title={t('credit.comment.title')}
              userId={user.id}
              incrementNumberOf={() => setNumberOfComments(old => old + 1)}
            />
          </>
          : numberOfComments}
      </Table.Td>
      {UserUtils.hasAuthority(ROLE_CR_R) &&
        <Table.Td ta={'center'}>
          {credit &&
            <>
              <Link onClick={() => setLinkVisible(true)}><IconLink size={16} stroke={1.5} /></Link>
              <PublicLinkModal
                opened={linkVisible}
                onClose={() => setLinkVisible(false)}
                create={() => CreditService.creditLinkCreate(credit.id)}
                requestId={REQUEST_CREDIT_LINK_CREATE_ID}
                t={t}
                link={credit.link}
                authority={ROLE_CR_C}
                object={'credit'}
              />
            </>
          }
        </Table.Td>
      }
      <Table.Td>
        {UserUtils.hasAuthority(ROLE_USR_AGT_REASSIGN) && isUnassigned ? (
          <>
            <Link onClick={() => setAgentVisible(true)}>{t("clients.unassigned.assignAgent")}</Link>
            <ClientAssignAgentModal
              opened={agentVisible}
              onClose={setAgentVisible}
              clientId={user.id}
              clientName={user.fullName}
              onAssigned={onAssigned}
              t={t}
            />
          </>
        ) : UserUtils.hasAuthority(ROLE_USR_AGT_REASSIGN) && agent ? (
          <>
            <Link onClick={() => setAgentVisible(true)}>{agent?.fullName}</Link>
            <AgentChangeModal
              opened={agentVisible}
              onClose={setAgentVisible}
              onSubmit={(newAgent: UserSimpleResponse) => UserService.userAgentChange(user.id, newAgent.id)}
              requestId={REQUEST_USER_AGENT_CHANGE_ID + user.id}
              t={t}
              agent={agent}
              afterSubmit={setAgent}
            />
          </>
        ) : agent?.fullName}
      </Table.Td>
    </Table.Tr>
  )
}

export default ClientListItem;
